import jsPDF from "jspdf";
import { toJpeg } from "html-to-image";
import { renderToStaticMarkup } from "react-dom/server";
import { getPagePx, paginateRows, NO_INVOICE_HTML } from "./pagination";

const renderToHtml = (nodeOrFn, arg1, arg2) => {
  if (!nodeOrFn) return "";
  let node;
  if (typeof nodeOrFn === "function") {
    // If 2-arg form provided, assume rowRenderer(item, index)
    if (typeof arg2 !== "undefined") node = nodeOrFn(arg1, arg2);
    else node = nodeOrFn(arg1 || {});
  } else {
    node = nodeOrFn;
  }
  try {
    return node ? renderToStaticMarkup(node) : "";
  } catch (e) {
    return "";
  }
};

// Infer column count from Tailwind classes like `grid-cols-9` or `grid-cols-[1fr_2fr_1fr]`
const inferGridColumns = (className = "", fallback = 2) => {
  if (!className) return fallback;
  const m = className.match(/grid-cols-(\d+)/);
  if (m) return parseInt(m[1], 10);
  const m2 = className.match(/grid-cols-\[(.+?)\]/);
  if (m2) {
    const tokens = m2[1].trim().split(/[ _]+/).filter(Boolean);
    return Math.max(1, tokens.length);
  }
  return fallback;
};

// Wait for all <img> elements inside a root element to finish loading/decoding
const waitForImages = async (root) => {
  if (!root) return;
  const imgs = Array.from(root.querySelectorAll("img"));
  if (!imgs.length) return;
  await Promise.all(
    imgs.map((img) => {
      // Ensure eager loading for offscreen elements
      try {
        img.loading = "eager";
      } catch (_) {}
      // If already complete, try decode (may throw for SVGs) or resolve
      if (img.complete) {
        if (typeof img.decode === "function") {
          return img.decode().catch(() => Promise.resolve());
        }
        return Promise.resolve();
      }
      // Otherwise listen for load/error
      return new Promise((resolve) => {
        img.onload = () => {
          if (typeof img.decode === "function") {
            img
              .decode()
              .then(() => resolve())
              .catch(() => resolve());
          } else {
            resolve();
          }
        };
        img.onerror = () => resolve();
      });
    })
  );
};

export async function generatePdf({
  data = null,
  initialPageSize = "a4",
  initialLayout = "portrait",
  rows = null,
  items = [],
  rowRenderer = null,
  header = null,
  headerTwo = null,
  contentTop = null,
  contentBottom = null,
  footer = null,
  footerTwo = null,
  tableHeader = null,
  backgroundLogo = null,
  bodyLayout = "list",
  gridColumns = 2,
  gridGap = 8,
  gridClassName = "",
  fileName,
} = {}) {
  const pageSize = initialPageSize;
  const layout = initialLayout;
  const bodyItems = rows || items || [];

  const { width: pagePxWidth, height: pagePxHeight } = getPagePx(
    pageSize,
    layout
  );

  // Measurement container (hidden)
  const measureRoot = document.createElement("div");
  measureRoot.style.cssText = `position:absolute;left:-9999px;top:-9999px;width:${pagePxWidth}px;visibility:hidden;`;
  document.body.appendChild(measureRoot);

  // Create measurement nodes
  const headerFirstEl = document.createElement("div");
  headerFirstEl.innerHTML = renderToHtml(header, {
    currentPage: 1,
    totalPages: 1,
  });
  measureRoot.appendChild(headerFirstEl);

  const headerOtherEl = document.createElement("div");
  headerOtherEl.innerHTML = renderToHtml(headerTwo, {
    currentPage: 2,
    totalPages: 2,
  });
  measureRoot.appendChild(headerOtherEl);

  const footerFirstEl = document.createElement("div");
  footerFirstEl.innerHTML = renderToHtml(footer, {
    currentPage: 1,
    totalPages: 1,
  });
  measureRoot.appendChild(footerFirstEl);

  const footerOtherEl = document.createElement("div");
  footerOtherEl.innerHTML = renderToHtml(footerTwo, {
    currentPage: 2,
    totalPages: 2,
  });
  measureRoot.appendChild(footerOtherEl);

  const contentTopEl = document.createElement("div");
  contentTopEl.innerHTML = renderToHtml(contentTop, {
    currentPage: 1,
    totalPages: 1,
  });
  measureRoot.appendChild(contentTopEl);

  const contentBottomEl = document.createElement("div");
  contentBottomEl.innerHTML = renderToHtml(contentBottom, {
    currentPage: 1,
    totalPages: 1,
  });
  measureRoot.appendChild(contentBottomEl);

  const bodyMeasure = document.createElement("div");
  measureRoot.appendChild(bodyMeasure);

  if (rowRenderer && bodyItems.length) {
    bodyItems.forEach((it, idx) => {
      const html = renderToHtml(rowRenderer, it, idx);
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      let rowEl;
      if (
        tmp.children.length === 1 &&
        tmp.firstElementChild &&
        tmp.firstElementChild.classList &&
        tmp.firstElementChild.classList.contains("measure-row")
      ) {
        // Use the provided root if it is already a measure-row
        rowEl = tmp.firstElementChild;
      } else {
        // Otherwise wrap the content in a single measure-row container
        const rowWrap = document.createElement("div");
        rowWrap.className = "measure-row";
        rowWrap.innerHTML = html;
        rowEl = rowWrap;
      }
      bodyMeasure.appendChild(rowEl);
    });
  } else {
    const empty = document.createElement("div");
    empty.innerHTML = NO_INVOICE_HTML;
    bodyMeasure.appendChild(empty);
  }

  // Split into pages using shared pagination logic
  const children = Array.from(bodyMeasure.children).filter(
    (el) => el.classList && el.classList.contains("measure-row")
  );
  let pages = [];

  if (!children.length) {
    pages = [[NO_INVOICE_HTML]];
  } else {
    const headerFirstH = headerFirstEl.offsetHeight || 60;
    const headerOtherH = headerOtherEl.offsetHeight || 30;
    const footerFirstH = footerFirstEl.offsetHeight || 80;
    const footerOtherH = footerOtherEl.offsetHeight || 40;
    const contentTopH = contentTopEl.offsetHeight || 0;
    const contentBottomH = contentBottomEl.offsetHeight || 40;

    const itemsArr = children.map((el) => ({
      height: el.offsetHeight,
      html: el.outerHTML,
    }));

    let rowsForPagination = itemsArr;
    if (bodyLayout === "grid") {
      rowsForPagination = [];
      const cols = inferGridColumns(gridClassName, gridColumns);
      const wrappers = [];
      for (let i = 0; i < itemsArr.length; i += cols) {
        const group = itemsArr.slice(i, i + cols);
        const wrapper = document.createElement("div");
        const needsGrid = gridClassName && !/\bgrid\b/.test(gridClassName);
        wrapper.className = `measure-row ${
          needsGrid ? "grid " : ""
        }${gridClassName}`.trim();
        if (!gridClassName) {
          wrapper.style.display = "grid";
          wrapper.style.gridTemplateColumns = `repeat(${cols}, 1fr)`;
          wrapper.style.gap = `${gridGap}px`;
        }
        group.forEach((g) => {
          const cell = document.createElement("div");
          cell.innerHTML = g.html;
          wrapper.appendChild(cell);
        });
        bodyMeasure.appendChild(wrapper);
        const rowH = wrapper.offsetHeight;
        const rowHtml = wrapper.outerHTML;
        rowsForPagination.push({ height: rowH, html: rowHtml });
        wrappers.push(wrapper);
      }
      // cleanup temporary measurement wrappers
      wrappers.forEach((w) => {
        try {
          bodyMeasure.removeChild(w);
        } catch (_) {}
      });
    }

    pages = paginateRows(rowsForPagination, pagePxHeight, {
      headerFirstH,
      headerOtherH,
      footerFirstH,
      footerOtherH,
      contentTopH,
      contentBottomH,
    });
  }

  // Visible container for capture (offscreen but visible)
  const visibleRoot = document.createElement("div");
  visibleRoot.style.cssText =
    "position:absolute;left:-9999px;top:-9999px;pointer-events:none;";
  document.body.appendChild(visibleRoot);

  const orientation = layout === "landscape" ? "l" : "p";
  const pdf = new jsPDF(orientation, "mm", pageSize);

  for (let p = 0; p < pages.length; p++) {
    const isFirst = p === 0;
    const isLast = p === pages.length - 1;

    const pageEl = document.createElement("div");
    pageEl.id = `pdf-page-${p}`;
    pageEl.className = "bg-white mx-auto mb-10 shadow-md";
    pageEl.style.width = `${pagePxWidth}px`;
    pageEl.style.height = `${pagePxHeight}px`;
    pageEl.style.display = "flex";
    pageEl.style.flexDirection = "column";
    pageEl.style.boxSizing = "border-box";
    pageEl.style.position = "relative";

    if (backgroundLogo) {
      const bgWrap = document.createElement("div");
      bgWrap.style.position = "absolute";
      bgWrap.style.top = "50%";
      bgWrap.style.left = "50%";
      bgWrap.style.transform = "translate(-50%, -50%)";
      bgWrap.style.zIndex = "0";
      bgWrap.style.opacity = "0.1";
      bgWrap.style.pointerEvents = "none";
      const img = document.createElement("img");
      img.src = backgroundLogo;
      img.alt = "Background Logo";
      img.style.maxWidth = `${pagePxWidth * 0.6}px`;
      img.style.maxHeight = `${pagePxHeight * 0.6}px`;
      img.style.objectFit = "contain";
      bgWrap.appendChild(img);
      pageEl.appendChild(bgWrap);
    }

    // Header
    const headerSec = document.createElement("div");
    headerSec.className = "p-0";
    headerSec.style.position = "relative";
    headerSec.style.zIndex = "1";
    headerSec.innerHTML = isFirst
      ? renderToHtml(header, { currentPage: p + 1, totalPages: pages.length })
      : renderToHtml(headerTwo, {
          currentPage: p + 1,
          totalPages: pages.length,
        });
    pageEl.appendChild(headerSec);

    // Content
    const contentSec = document.createElement("div");
    contentSec.className = "flex-1";
    contentSec.style.overflow = "hidden";
    contentSec.style.position = "relative";
    contentSec.style.zIndex = "1";

    if (isFirst && contentTop) {
      const topEl = document.createElement("div");
      topEl.className = "mb-4";
      topEl.innerHTML = renderToHtml(contentTop, {
        currentPage: p + 1,
        totalPages: pages.length,
      });
      contentSec.appendChild(topEl);
    }

    if (tableHeader) {
      const th = document.createElement("div");
      th.className = "mt-2";
      th.innerHTML = renderToHtml(tableHeader, {
        currentPage: p + 1,
        totalPages: pages.length,
      });
      contentSec.appendChild(th);
    }

    const bodyEl = document.createElement("div");
    const headerFirstH = headerFirstEl.offsetHeight || 60;
    const headerOtherH = headerOtherEl.offsetHeight || 30;
    const footerFirstH = footerFirstEl.offsetHeight || 80;
    const footerOtherH = footerOtherEl.offsetHeight || 40;
    const headerH = isFirst ? headerFirstH : headerOtherH;
    const footerH = isLast ? footerFirstH : footerOtherH;
    const usableBodyH = Math.max(0, pagePxHeight - headerH - footerH);
    bodyEl.style.height = isFirst || isLast ? "auto" : `${usableBodyH}px`;
    bodyEl.style.overflow = "hidden";
    bodyEl.style.boxSizing = "border-box";
    bodyEl.innerHTML = pages[p].join("");
    contentSec.appendChild(bodyEl);

    if (isLast && contentBottom) {
      const botEl = document.createElement("div");
      botEl.className = "mt-4";
      botEl.innerHTML = renderToHtml(contentBottom, {
        currentPage: p + 1,
        totalPages: pages.length,
      });
      contentSec.appendChild(botEl);
    }

    pageEl.appendChild(contentSec);

    // Footer
    const footerSec = document.createElement("div");
    footerSec.className = "";
    footerSec.style.position = "relative";
    footerSec.style.zIndex = "1";
    footerSec.innerHTML = isLast
      ? renderToHtml(footer, { currentPage: p + 1, totalPages: pages.length })
      : renderToHtml(footerTwo, {
          currentPage: p + 1,
          totalPages: pages.length,
        });
    pageEl.appendChild(footerSec);

    visibleRoot.appendChild(pageEl);

    try {
      const imgs = Array.from(pageEl.querySelectorAll("img"));
      await Promise.all(
        imgs.map(async (img) => {
          const src = img.getAttribute("src") || "";
          if (!src.trim()) {
            img.remove();
            return;
          }
          let isCross = false;
          try {
            const u = new URL(src, window.location.href);
            isCross = u.origin !== window.location.origin;
          } catch (_) {}
          if (isCross && !src.startsWith("data:")) {
            try {
              const res = await fetch(src, { mode: "cors", credentials: "omit" });
              if (!res.ok) throw new Error("fetch failed");
              const blob = await res.blob();
              await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                  img.setAttribute("src", reader.result);
                  img.setAttribute("crossorigin", "anonymous");
                  resolve();
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
            } catch (_) {
              img.remove();
            }
          }
        })
      );
    } catch (_) {}

    let imgData;
    try {
      imgData = await toJpeg(pageEl, {
        quality: 1.0,
        backgroundColor: "#ffffff",
        width: pagePxWidth,
        height: pagePxHeight,
        skipFonts: true,
        pixelRatio: Math.max(2, window.devicePixelRatio || 1),
        cacheBust: true,
        style: { margin: 0, boxShadow: "none" },
      });
    } catch (_) {
      try {
        Array.from(pageEl.querySelectorAll("img")).forEach((img) => img.remove());
        imgData = await toJpeg(pageEl, {
          quality: 1.0,
          backgroundColor: "#ffffff",
          width: pagePxWidth,
          height: pagePxHeight,
          skipFonts: true,
          pixelRatio: Math.max(2, window.devicePixelRatio || 1),
          cacheBust: true,
          style: { margin: 0, boxShadow: "none" },
        });
      } catch (err) {
        continue;
      }
    }

    const w = pdf.internal.pageSize.getWidth();
    const h = pdf.internal.pageSize.getHeight();
    pdf.addImage(imgData, "JPEG", 0, 0, w, h, undefined, "FAST");
    if (p < pages.length - 1) pdf.addPage();
  }

  const finalName =
    fileName ||
    `invoice_${
      data?.invoiceNumber || data?.invoiceDeta?.invoiceNumber || "00001"
    }_${Date.now()}.pdf`;
  pdf.save(finalName);

  // Cleanup
  try {
    document.body.removeChild(measureRoot);
  } catch (_) {}
  try {
    document.body.removeChild(visibleRoot);
  } catch (_) {}
}

export default generatePdf;
